import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import LoadingSpinner from '../components/common/LoadingSpinner/LoadingSpinner';

/**
 * Maps a COSMIC role to its URL segment used by the role route trees in App.js.
 */
export const ROLE_SEGMENT = {
  super_admin: 'super-admin',
  manager: 'manager',
  mentor: 'mentor',
  student: 'student',
  counsellor: 'counsellor',
  poc_owner: 'poc-owner',
};

/**
 * Which roles actually have a list page for a given deep-link kind.
 * Counsellors and POC owners only have Projects + a couple of pages.
 */
const KIND_ROLES = {
  assignments: ['super_admin', 'manager', 'mentor', 'student'],
  classes: ['super_admin', 'manager', 'mentor', 'student'],
  disputes: ['super_admin', 'manager', 'mentor', 'student'],
  competitions: ['student'],
};

/**
 * Resolves the role-agnostic deep links that the backend generates
 * (e.g. `/assignments/:id`, `/classes/:id`, `/projects/:id`, `/disputes/:id`
 * from reminder_service._deep_link) into the correct role-prefixed route.
 *
 * - While auth is resolving: show a spinner (never a blank screen).
 * - Logged out: stash the intended URL in sessionStorage and send to /login.
 *   AuthContext.login() consumes it after a successful sign-in.
 * - Logged in: redirect to the role-correct page. `projects` has a real
 *   detail route for every role; the others fall back to their list page,
 *   or to the role home if that role has no such list.
 */
const DeepLinkResolver = ({ kind }) => {
  const { id } = useParams();
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    try {
      const target = window.location.pathname + window.location.search;
      if (target.startsWith('/') && !target.startsWith('//')) {
        sessionStorage.setItem('returnTo', target);
      }
    } catch (e) {
      // sessionStorage unavailable — fall through to plain /login
    }
    return <Navigate to="/login" replace />;
  }

  const segment = ROLE_SEGMENT[user.role];
  if (!segment) {
    return <Navigate to="/login" replace />;
  }

  if (kind === 'projects' && id) {
    return <Navigate to={`/${segment}/projects/${id}`} replace />;
  }

  const roleHasList = KIND_ROLES[kind]?.includes(user.role);
  if (roleHasList) {
    return <Navigate to={`/${segment}/${kind}`} replace />;
  }

  // Role has no page for this kind — send them to their home.
  return <Navigate to={`/${segment}`} replace />;
};

export default DeepLinkResolver;
